import { request } from '@umijs/max';

/** A pending "you were automatically clocked out" notice — see CsrClockAutoCloseService. */
export type AutoCloseNotice = {
  session_id: number;
  clocked_in_at: string;
  clocked_out_at: string;
  reason: string | null;
};

export type ClockStatus = {
  clocked_in: boolean;
  clocked_in_at: string | null;
  /** True once there's any clock session for today's business date, in or out. */
  clocked_in_today: boolean;
  /** Set until the CSR dismisses it via acknowledgeAutoClose(). */
  auto_close_notice: AutoCloseNotice | null;
};

/** Whether the current user is currently clocked in, and since when. */
export async function getClockStatus(): Promise<ClockStatus> {
  return request<{ success: boolean; data: ClockStatus }>(
    '/api/tenant/csr/clock/status',
    { method: 'GET', withToken: true },
  ).then((res) => res.data);
}

/** Starts a session. A no-op (returns the existing session) if already clocked in. */
export async function clockIn(): Promise<ClockStatus> {
  return request<{ success: boolean; data: ClockStatus }>(
    '/api/tenant/csr/clock/in',
    { method: 'POST', withToken: true },
  ).then((res) => res.data);
}

/** Closes the open session. A no-op if not clocked in. */
export async function clockOut(): Promise<ClockStatus> {
  return request<{ success: boolean; data: ClockStatus }>(
    '/api/tenant/csr/clock/out',
    { method: 'POST', withToken: true },
  ).then((res) => res.data);
}

/** Dismisses a pending auto-close notice so it stops resurfacing. */
export async function acknowledgeAutoClose(sessionId: number): Promise<void> {
  await request('/api/tenant/csr/clock/auto-close/acknowledge', {
    method: 'POST',
    withToken: true,
    data: { session_id: sessionId },
  });
}
